import { useState } from "react";
import { Award, CalendarClock, Crown, PlusCircle, Scale, Zap } from "lucide-react";
import { useOrganization } from "../organization/useOrganization";
import { Button } from "../../shared/ui/Button";
import { Card, StatCard } from "../../shared/ui/Card";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import { ShopAdminPanel } from "../shop/ShopAdminPanel";
import { useIncentiveActions, useIncentives } from "./useIncentives";
export function IncentiveCenterPage() {
  const org = useOrganization(),
    query = useIncentives(),
    actions = useIncentiveActions();
  const [name, setName] = useState(""),
    [description, setDescription] = useState(""),
    [points, setPoints] = useState(50),
    [employeeId, setEmployeeId] = useState(""),
    [adjustment, setAdjustment] = useState(0),
    [reason, setReason] = useState(""),
    [honorEmployeeId, setHonorEmployeeId] = useState(""),
    [honorTitle, setHonorTitle] = useState(""),
    [honorDescription, setHonorDescription] = useState(""),
    [honorPoints, setHonorPoints] = useState(0),
    [clearingYear, setClearingYear] = useState(new Date().getFullYear() - 1),
    [carryoverLimit, setCarryoverLimit] = useState(0),
    [notice, setNotice] = useState(""),
    [error, setError] = useState("");
  if (!org.data || !query.data) return <PageSkeleton />;
  const employees = org.data.accounts.filter(
      (x) => x.role === "employee" && x.status === "active",
    ),
    names = Object.fromEntries(org.data.accounts.map((x) => [x.id, x.name])),
    ranking = Object.entries(
      query.data.ledger.reduce<Record<string, number>>(
        (totals, row) => ({ ...totals, [row.employeeId]: (totals[row.employeeId] ?? 0) + row.changePoints }),
        {},
      ),
    )
      .map(([rankingEmployeeId, rankingPoints]) => ({ employeeId: rankingEmployeeId, points: rankingPoints }))
      .sort((a, b) => b.points - a.points);
  const run = async (fn: () => Promise<unknown>, message: string) => {
    setNotice("");
    setError("");
    try {
      await fn();
      setNotice(message);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "操作失败");
    }
  };
  return (
    <div className="page-stack">
      <header className="page-heading">
        <div>
          <span>总部激励治理</span>
          <h1>激励中心</h1>
          <p>统一规则、审批流水和排行榜；任何余额变化都必须有来源。</p>
        </div>
        <StatusBadge tone="success">全公司</StatusBadge>
      </header>
      {(notice || error) && (
        <div
          className={
            error ? "inline-notice inline-notice--error" : "inline-notice"
          }
        >
          {error || notice}
        </div>
      )}
      <div className="stat-grid">
        <StatCard
          label="启用规则"
          value={`${query.data.rules.filter((x) => x.active).length}`}
          detail="全公司统一口径"
        />
        <StatCard
          label="待审批"
          value={`${query.data.claims.filter((x) => x.status === "pending").length}`}
          detail="门店店长负责"
        />
        <StatCard
          label="已发放星能"
          value={`${query.data.ledger.filter((x) => x.changePoints > 0).reduce((n, x) => n + x.changePoints, 0)}`}
          detail="不可覆盖流水"
        />
      </div>
      <div className="management-grid">
        <Card className="management-card">
          <div className="card-heading">
            <PlusCircle />
            <div>
              <span>总部专属</span>
              <h2>新增星能规则</h2>
            </div>
          </div>
          <div className="v4-form">
            <label className="v4-field">
              <span>规则名称</span>
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="v4-field">
              <span>核验说明</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            <label className="v4-field">
              <span>奖励星能</span>
              <input
                type="number"
                value={points}
                onChange={(e) => setPoints(Number(e.target.value))}
              />
            </label>
            <Button
              onClick={() =>
                run(
                  () =>
                    actions.createRule({
                      name,
                      description,
                      points,
                      scope: "company",
                      storeId: null,
                    }),
                  "星能规则已创建。",
                )
              }
            >
              <PlusCircle size={15} />
              创建规则
            </Button>
          </div>
        </Card>
        <Card className="management-card">
          <div className="card-heading">
            <Scale />
            <div>
              <span>审计留痕</span>
              <h2>人工调整</h2>
            </div>
          </div>
          <div className="v4-form">
            <label className="v4-field">
              <span>员工</span>
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              >
                <option value="">请选择</option>
                {employees.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="v4-field">
              <span>调整值（可为负数）</span>
              <input
                type="number"
                value={adjustment}
                onChange={(e) => setAdjustment(Number(e.target.value))}
              />
            </label>
            <label className="v4-field">
              <span>调整原因</span>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <Button
              onClick={() =>
                run(
                  () =>
                    actions.adjust({ employeeId, points: adjustment, reason }),
                  "星能调整已写入流水。",
                )
              }
              disabled={!employeeId}
            >
              <Scale size={15} />
              确认调整
            </Button>
          </div>
        </Card>
      </div>
      <div className="management-grid">
        <Card className="management-card"><div className="card-heading"><Award /><div><span>总部授予 · 自动留痕</span><h2>员工荣誉</h2></div></div><div className="v4-form"><label className="v4-field"><span>员工</span><select value={honorEmployeeId} onChange={(event) => setHonorEmployeeId(event.target.value)}><option value="">请选择</option>{employees.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><label className="v4-field"><span>荣誉名称</span><input value={honorTitle} onChange={(event) => setHonorTitle(event.target.value)} placeholder="例如：年度最佳教练" /></label><label className="v4-field"><span>授予说明</span><textarea value={honorDescription} onChange={(event) => setHonorDescription(event.target.value)} /></label><label className="v4-field"><span>同步奖励星能</span><input type="number" min="0" value={honorPoints} onChange={(event) => setHonorPoints(Number(event.target.value))} /></label><Button disabled={!honorEmployeeId || actions.pending} onClick={() => run(() => actions.awardHonor({ employeeId: honorEmployeeId, title: honorTitle, description: honorDescription, badge: "🏆", points: honorPoints }), "员工荣誉已授予并同步到荣誉墙。")}>授予荣誉</Button></div></Card>
        <Card className="management-card"><div className="card-heading"><CalendarClock /><div><span>高风险操作 · 每年仅一次</span><h2>年度星能清算</h2></div></div><p className="muted-copy">系统保留全部历史流水，只追加一笔清算流水，将每位员工余额降至结转上限。执行后不能再次清算同一年度。</p><div className="v4-form"><label className="v4-field"><span>清算年度</span><input type="number" min="2020" max={new Date().getFullYear()} value={clearingYear} onChange={(event) => setClearingYear(Number(event.target.value))} /></label><label className="v4-field"><span>每人结转上限</span><input type="number" min="0" value={carryoverLimit} onChange={(event) => setCarryoverLimit(Number(event.target.value))} /></label><Button variant="danger" disabled={actions.pending} onClick={() => { if (window.confirm(`确认执行${clearingYear}年度清算？该操作会影响全体员工星能余额。`)) run(() => actions.runAnnualClearing({ year: clearingYear, carryoverLimit }), "年度星能清算完成，结果已写入不可覆盖流水。"); }}>确认执行年度清算</Button></div>{query.data.clearings.map((item) => <div className="clearing-result" key={item.id}><strong>{item.year}年度</strong><span>{item.employeeCount}人 · 清算{item.clearedPoints}星能 · 结转上限{item.carryoverLimit}</span></div>)}</Card>
      </div>
      <div className="management-grid">
        <Card className="management-card energy-rules">
          <div className="card-heading">
            <Zap />
            <div>
              <span>统一口径</span>
              <h2>星能规则</h2>
            </div>
          </div>
          {query.data.rules.map((r) => (
            <article key={r.id}>
              <span>
                <Zap size={15} />
              </span>
              <div>
                <strong>{r.name}</strong>
                <p>{r.description}</p>
              </div>
              <b>+{r.points}</b>
            </article>
          ))}
        </Card>
        <Card className="management-card ranking-card">
          <div className="card-heading">
            <Crown />
            <div>
              <span>流水实时汇总</span>
              <h2>员工排行榜</h2>
            </div>
          </div>
          {ranking.map((row, index) => (
            <article key={row.employeeId}>
              <span>{index + 1}</span>
              <strong>{names[row.employeeId]}</strong>
              <b>{row.points}</b>
            </article>
          ))}
        </Card>
      </div>
      <ShopAdminPanel />
    </div>
  );
}
